import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { PageResponse } from '../models/producto.model';
import {
  ProductBundleRequest,
  ProductBundleResponse,
  ProductBundleSearchParams,
  ProductBundleItemRequest,
  ProductBundleItemResponse
} from '../models/product-bundle.model';
// Inc 35/9: timeline del paquete reutiliza el shape de auditoría.
import { AuditLogResponse, PageResponse as TimelinePageResponse } from '../../audit/models/audit.model';

@Injectable({
  providedIn: 'root'
})
export class ProductBundleApiService {
  private readonly apiUrl = `${environment.apiUrl}api/product-bundles`;

  constructor(private http: HttpClient) { }

  searchProductBundle(params: ProductBundleSearchParams): Observable<PageResponse<ProductBundleResponse>> {
    let httpParams = new HttpParams().set('businessId', params.businessId.toString());
    if (params.searchName) httpParams = httpParams.set('searchName', params.searchName);
    if (params.searchSku) httpParams = httpParams.set('searchSku', params.searchSku);
    if (params.isActive !== undefined) httpParams = httpParams.set('isActive', params.isActive.toString());
    if (params.page !== undefined) httpParams = httpParams.set('page', params.page.toString());
    if (params.size !== undefined) httpParams = httpParams.set('size', params.size.toString());
    return this.http.get<PageResponse<ProductBundleResponse>>(this.apiUrl, { params: httpParams });
  }

  getProductBundleById(id: number, businessId: number): Observable<ProductBundleResponse> {
    const params = new HttpParams().set('businessId', businessId.toString());
    return this.http.get<ProductBundleResponse>(`${this.apiUrl}/${id}`, { params });
  }

  createProductBundle(bundle: ProductBundleRequest): Observable<ProductBundleResponse> {
    return this.http.post<ProductBundleResponse>(this.apiUrl, bundle);
  }

  updateProductBundle(id: number, businessId: number, bundle: ProductBundleRequest): Observable<ProductBundleResponse> {
    const params = new HttpParams().set('businessId', businessId.toString());
    return this.http.put<ProductBundleResponse>(`${this.apiUrl}/${id}`, bundle, { params });
  }

  deleteProductBundle(id: number, businessId: number): Observable<void> {
    const params = new HttpParams().set('businessId', businessId.toString());
    return this.http.delete<void>(`${this.apiUrl}/${id}`, { params });
  }

  /**
   * Inc 35/9: GET /api/product-bundles/{id}/timeline → PageResponse<EntityAuditEventResponse>.
   * Incluye creación, cambios de campos y ALTA/BAJA de productos (operationSubtype ADD_ITEM/UPDATE_ITEM/REMOVE_ITEM).
   */
  getBundleTimeline(id: number, page: number, size: number): Observable<TimelinePageResponse<AuditLogResponse>> {
    const params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString());
    return this.http.get<TimelinePageResponse<AuditLogResponse>>(
      `${this.apiUrl}/${id}/timeline`, { params });
  }

  // Bundle items

  getBundleItems(bundleId: number, businessId: number): Observable<ProductBundleItemResponse[]> {
    const params = new HttpParams().set('businessId', businessId.toString());
    return this.http.get<ProductBundleItemResponse[]>(`${this.apiUrl}/${bundleId}/items`, { params });
  }

  getBundleItemById(bundleId: number, id: number, businessId: number): Observable<ProductBundleItemResponse> {
    const params = new HttpParams().set('businessId', businessId.toString());
    return this.http.get<ProductBundleItemResponse>(`${this.apiUrl}/${bundleId}/items/${id}`, { params });
  }

  createBundleItem(bundleId: number, businessId: number, item: ProductBundleItemRequest): Observable<ProductBundleItemResponse> {
    const params = new HttpParams().set('businessId', businessId.toString());
    return this.http.post<ProductBundleItemResponse>(`${this.apiUrl}/${bundleId}/items`, item, { params });
  }

  updateBundleItem(bundleId: number, id: number, businessId: number, item: ProductBundleItemRequest): Observable<ProductBundleItemResponse> {
    const params = new HttpParams().set('businessId', businessId.toString());
    return this.http.put<ProductBundleItemResponse>(`${this.apiUrl}/${bundleId}/items/${id}`, item, { params });
  }

  deleteBundleItem(bundleId: number, id: number, businessId: number): Observable<void> {
    const params = new HttpParams().set('businessId', businessId.toString());
    return this.http.delete<void>(`${this.apiUrl}/${bundleId}/items/${id}`, { params });
  }
}
